/**
 * Active-brand accessor.
 *
 * Brand is a build-time variable (`VITE_APP_BRAND`), resolved in `vite.config`
 * and read here via `import.meta.env`. There is no runtime brand switching.
 */
import activeLogo from '@brand-asset/full-logo.png'

import { BRANDS } from './brand.registry'
import type { IBrandConfig, BrandKey } from './brand.types'

const DEFAULT_BRAND: BrandKey = 'peraso'

function isBrandKey(value: string | undefined): value is BrandKey {
  return value === 'peraso' || value === 'dune'
}

/** Resolve the active brand key from the build variable; default `peraso`. */
export function activeBrandKey(): BrandKey {
  const raw = import.meta.env.VITE_APP_BRAND
  if (isBrandKey(raw)) {
    return raw
  }
  if (raw) {
    console.warn(
      `[brand] Unknown VITE_APP_BRAND "${raw}" — falling back to "${DEFAULT_BRAND}".`,
    )
  }
  return DEFAULT_BRAND
}

/** Resolve the full config for the active brand. */
export function getActiveBrand(): IBrandConfig {
  // logo is the build-resolved active-brand asset (see the @brand-asset alias
  // in vite.config); only the active brand's logo is ever bundled.
  return { ...BRANDS[activeBrandKey()], logo: activeLogo }
}

/** Convenience singleton for app code (brand is fixed per build). */
export const brand: IBrandConfig = getActiveBrand()
